/**
 * Os parâmetros de busca da /agenda (`?aba=recurso&view=month&date=2026-08-14`)
 * — módulo PURO (sem React, sem alias `@/`), porque a guarda da API
 * (apps/api/src/shared/agenda-plates-source.test.ts) o importa para provar a
 * aba na URL (spec 039).
 *
 * `aba`: "geral" é a Agenda de sempre; "recurso" é a agenda por placa. O valor
 * da URL é neutro de propósito — a palavra do vertical vem do perfil do tenant,
 * e a guarda de vocabulário reprova "placa" escrito no SPA (a barra de
 * endereço também é tela). Valor desconhecido ou ausente cai em "geral": link
 * antigo sem `aba` abre a Agenda, como antes.
 *
 * `ocultos` (adendo da 039): o switch "Mostrar clientes ocultos" da aba por
 * placa — `1` liga, `0` desliga. Ausente (ou desconhecido), vale a última
 * escolha lembrada no navegador; sem ela, desligado, que respeita a ocultação
 * do tenant. Número porque o TanStack Router faz JSON.parse do valor: com
 * string, a URL sairia `ocultos=%221%22`.
 */
import { z } from 'zod';

export const agendaSearchSchema = z.object({
  aba: z.enum(['geral', 'recurso']).catch('geral').default('geral'),
  view: z.enum(['month', 'week', 'day', 'list']).default('month'),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  ocultos: z.coerce
    .number()
    .pipe(z.union([z.literal(0), z.literal(1)]))
    .optional()
    .catch(undefined),
});
export type AgendaSearch = z.infer<typeof agendaSearchSchema>;

/** A chave do lembrado no navegador — por navegador, nunca no servidor. */
export const SHOW_HIDDEN_KEY = 'erp.agenda.ocultos';

type PrefStorage = Pick<Storage, 'getItem' | 'setItem'>;

/**
 * A última escolha do switch. `storage` é uma função porque o próprio acesso a
 * `window.localStorage` lança com os dados do site bloqueados — dentro do
 * try, então. Sem storage, ilegível ou nunca gravado: desligado.
 */
export function rememberedShowHidden(storage: () => PrefStorage | undefined): boolean {
  try {
    return storage()?.getItem(SHOW_HIDDEN_KEY) === '1';
  } catch {
    return false;
  }
}

/** Grava a escolha; falhar ao gravar não é erro de tela — a URL ainda vale. */
export function rememberShowHidden(storage: () => PrefStorage | undefined, on: boolean): void {
  try {
    storage()?.setItem(SHOW_HIDDEN_KEY, on ? '1' : '0');
  } catch {
    // aba privada, cota cheia, dados bloqueados: fica só a URL
  }
}

/** A URL vence o lembrado; sem nenhum dos dois, desligado. */
export function showHiddenOf(fromUrl: AgendaSearch['ocultos'], remembered: boolean): boolean {
  return fromUrl === undefined ? remembered : fromUrl === 1;
}
